import React, { useState, useEffect, useMemo } from 'react';
import { fetchSkillUsageStats, type SkillUsageStat } from '../../lib/referentialService';

const SkillUsage: React.FC = () => {
  const [stats, setStats] = useState<SkillUsageStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'checked' | 'unchecked' | 'ratio'>('checked');

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchSkillUsageStats(100);
        setStats(data);
      } catch (err) {
        console.error('Failed to load skill usage stats', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sortedStats = useMemo(() => {
    const copy = [...stats];
    if (sortBy === 'checked') copy.sort((a, b) => b.checkedCount - a.checkedCount);
    else if (sortBy === 'unchecked') copy.sort((a, b) => b.uncheckedCount - a.uncheckedCount);
    else copy.sort((a, b) => a.ratio - b.ratio);
    return copy;
  }, [stats, sortBy]);

  const topConfirmed = useMemo(() =>
    [...stats].sort((a, b) => b.checkedCount - a.checkedCount).slice(0, 20),
    [stats]
  );

  const topUnchecked = useMemo(() =>
    [...stats].sort((a, b) => b.uncheckedCount - a.uncheckedCount).slice(0, 20),
    [stats]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Usage des compétences</h1>
        <span className="text-sm text-gray-500">{stats.length} compétences avec données d'usage</span>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Total cochées</div>
          <div className="text-3xl font-bold text-green-600">
            {stats.reduce((s, r) => s + r.checkedCount, 0).toLocaleString()}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Total décochées</div>
          <div className="text-3xl font-bold text-red-500">
            {stats.reduce((s, r) => s + r.uncheckedCount, 0).toLocaleString()}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Ratio moyen</div>
          <div className="text-3xl font-bold text-[#00244F]">
            {stats.length > 0 ? (stats.reduce((s, r) => s + r.ratio, 0) / stats.length * 100).toFixed(1) : '0'}%
          </div>
        </div>
      </div>

      {/* Top 20 grids */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-green-50">
            <h3 className="text-sm font-semibold text-green-800">Top 20 — Plus confirmées</h3>
          </div>
          <div className="divide-y divide-gray-50">
            {topConfirmed.map((s, i) => (
              <div key={s.skillId} className="px-5 py-2.5 flex items-center gap-3">
                <span className="text-xs text-gray-400 w-5 text-right">{i + 1}</span>
                <span className="text-sm font-medium text-gray-900 flex-1 truncate">{s.skillName}</span>
                <span className="text-xs font-mono text-green-700 bg-green-50 px-2 py-0.5 rounded">
                  {s.checkedCount}
                </span>
                <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full" style={{ width: `${s.ratio * 100}%` }} />
                </div>
              </div>
            ))}
            {topConfirmed.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-gray-400">Aucune donnée</div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-red-50">
            <h3 className="text-sm font-semibold text-red-800">Top 20 — Plus décochées (non-pertinence)</h3>
          </div>
          <div className="divide-y divide-gray-50">
            {topUnchecked.map((s, i) => (
              <div key={s.skillId} className="px-5 py-2.5 flex items-center gap-3">
                <span className="text-xs text-gray-400 w-5 text-right">{i + 1}</span>
                <span className="text-sm font-medium text-gray-900 flex-1 truncate">{s.skillName}</span>
                <span className="text-xs font-mono text-red-700 bg-red-50 px-2 py-0.5 rounded">
                  {s.uncheckedCount}
                </span>
                <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-red-400 rounded-full" style={{ width: `${(1 - s.ratio) * 100}%` }} />
                </div>
              </div>
            ))}
            {topUnchecked.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-gray-400">Aucune donnée</div>
            )}
          </div>
        </div>
      </div>

      {/* Full table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Toutes les compétences (avec données)</h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Trier par :</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-2 py-1 border border-gray-200 rounded text-xs focus:outline-none"
            >
              <option value="checked">Plus cochées</option>
              <option value="unchecked">Plus décochées</option>
              <option value="ratio">Ratio le plus bas</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">#</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Compétence</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">Cochées</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">Décochées</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">Ratio</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sortedStats.map((s, i) => (
                <tr key={s.skillId} className="hover:bg-gray-50/50">
                  <td className="px-4 py-2 text-xs text-gray-400">{i + 1}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{s.skillName}</td>
                  <td className="px-4 py-2 text-sm text-right text-green-700 font-mono">{s.checkedCount}</td>
                  <td className="px-4 py-2 text-sm text-right text-red-600 font-mono">{s.uncheckedCount}</td>
                  <td className="px-4 py-2 text-sm text-right font-mono">
                    <span className={s.ratio < 0.5 ? 'text-red-600' : 'text-green-700'}>
                      {(s.ratio * 100).toFixed(0)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SkillUsage;
